import { Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { UserRead } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";
import { listToString } from "@nens/shared-utils";

import { cancelButtonText } from "@/translations";

import { useUpdateUsers } from "../api/update-tenant-user";

interface TenantUserDeactivateModalProps {
  users: UserRead[];
  onClose: () => void;
}

export function TenantUserDeactivateModal({ users, onClose }: TenantUserDeactivateModalProps) {
  const intl = useIntl();
  const { mutate, isMutating, isError } = useUpdateUsers(users.map(({ id }) => id));
  const multiple = users.length > 1;

  const deactivateUsers = () => {
    mutate({ active: false }, { onSuccess: onClose });
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <>
          <FormattedMessage id="header.deactivate_user" defaultMessage="Deactivate user account" />
          {multiple && "s"}
        </>
      }
      maxW="xl"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: intl.formatMessage(
            {
              id: "button.deactivate_user",
              defaultMessage: "Deactivate user account{multiple, select, true {s} other {}}",
            },
            { multiple }
          ),
          variant: "danger",
          isDisabled: isMutating,
          onClick: deactivateUsers,
        },
      ]}
    >
      <Flex flexDir="column" gap={2}>
        <Text>
          <FormattedMessage
            id="text.user_email"
            defaultMessage="Email address{multiple, select, true {es} other {}}: "
            values={{ multiple }}
          />
          <Text as="span" color="brand.deep_sea" fontWeight="semibold">
            {listToString(users.map(({ email }) => email))}
          </Text>
        </Text>
        <Text>
          <FormattedMessage
            id="paragraph.deactivate_user"
            defaultMessage="Deactivated users lose access to their data and projects within this organisation."
          />
        </Text>
        {/* FUTURE: This option is not working yet.
        <Checkbox whiteSpace="nowrap">
          <FormattedMessage
            id="checkbox.send_deactivation_mail"
            defaultMessage="Send the user{multiple, select, true {s} other {}} an email about account deactivation"
            values={{ multiple }}
          />
        </Checkbox> */}
        {isMutating && <RanaLoading size="md" isCentered />}
        {isError && (
          <Text color="red.500">
            <FormattedMessage
              id="error.deactivate_user_failed"
              defaultMessage="Failed to deactivate user account{multiple, select, true {s} other {}}. Please try again."
              values={{ multiple }}
            />
          </Text>
        )}
      </Flex>
    </RanaModal>
  );
}
